// modals/AddAbstractModal.js
import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/system';

// Full-screen backdrop that dims the background.
const ModalBackdrop = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
});

// Modal card container with ample padding and scrollability if needed.
const ModalCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  width: '90%',
  maxWidth: 800,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: theme.shadows[5],
}));

// A custom single-line input component.
const CustomInputField = styled(({ label, error, helperText, ...props }) => (
  <Box sx={{ mb: 2 }}>
    {label && (
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {label}
      </Typography>
    )}
    <input
      {...props}
      style={{
        width: '100%',
        padding: '12px',
        fontSize: '1rem',
        fontFamily: 'inherit',
        border: error ? '1px solid #d32f2f' : '1px solid #ccc',
        borderRadius: '4px',
        boxSizing: 'border-box',
      }}
    />
    {error && helperText && (
      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
        {helperText}
      </Typography>
    )}
  </Box>
))(({ theme }) => ({}));

// A custom multi-line text area component that expands dynamically.
const CustomTextArea = styled(({ label, error, helperText, ...props }) => (
  <Box sx={{ mb: 2 }}>
    {label && (
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {label}
      </Typography>
    )}
    <textarea
      {...props}
      style={{
        width: '100%',
        padding: '12px',
        fontSize: '1rem',
        fontFamily: 'inherit',
        border: error ? '1px solid #d32f2f' : '1px solid #ccc',
        borderRadius: '4px',
        resize: 'vertical',
        minHeight: '150px',
        lineHeight: 1.5,
        boxSizing: 'border-box',
      }}
    />
    {error && helperText && (
      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
        {helperText}
      </Typography>
    )}
  </Box>
))(({ theme }) => ({}));

function AddAbstractModal({ open, onClose, onSave }) {
  // Define maximum character limits.
  const maxTitleLength = 500;
  const maxContentLength = 3000;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [titleError, setTitleError] = useState('');
  const [contentError, setContentError] = useState('');

  // Reset the form when the modal opens.
  useEffect(() => {
    if (open) {
      setTitle('');
      setContent('');
      setTitleError('');
      setContentError('');
    }
  }, [open]);

  const handleSave = () => {
    let hasError = false;
    if (!title.trim()) {
      setTitleError('Title is required.');
      hasError = true;
    }
    if (!content.trim()) {
      setContentError('Content is required.');
      hasError = true;
    }
    if (hasError) return;
    onSave({ title: title.trim(), content: content.trim() });
    onClose();
  };

  // Do not render if modal is not open.
  if (!open) return null;

  return (
    <ModalBackdrop onClick={onClose}>
      {/* Prevent clicks inside the modal card from closing the modal */}
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Add Abstract
        </Typography>
        <CustomInputField
          label="Abstract Title"
          placeholder="Enter abstract title..."
          value={title}
          onChange={(e) => {
            if (e.target.value.length <= maxTitleLength) {
              setTitle(e.target.value);
              if (e.target.value.trim()) setTitleError('');
            }
          }}
          error={Boolean(titleError)}
          helperText={titleError}
          maxLength={maxTitleLength}
        />
        <Typography variant="caption" sx={{ textAlign: 'right', mb: 1 }}>
          {title.length}/{maxTitleLength} characters
        </Typography>
        <CustomTextArea
          label="Abstract Content"
          placeholder="Enter abstract content..."
          value={content}
          onChange={(e) => {
            if (e.target.value.length <= maxContentLength) {
              setContent(e.target.value);
              if (e.target.value.trim()) setContentError('');
            }
          }}
          error={Boolean(contentError)}
          helperText={contentError}
          maxLength={maxContentLength}
        />
        <Typography variant="caption" sx={{ textAlign: 'right', mb: 1 }}>
          {content.length}/{maxContentLength} characters
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} color="primary">
            Add
          </Button>
        </Box>
      </ModalCard>
    </ModalBackdrop>
  );
}

export default AddAbstractModal;
